
import { Typography } from 'antd'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { loginSchema } from '@/api/auth.api'
import { useLogin } from '@/hooks/use-login'
import { useForm } from '@tanstack/react-form'
import type { Updater } from '@tanstack/router-core'
import { PasswordField } from './PasswordField'


const { Text } = Typography

interface LoginFormViewProps {
  redirectTo: string
  onForgotPassword: () => void
}

export function LoginFormView({
  redirectTo,
  onForgotPassword,
}: LoginFormViewProps) {
  const login = useLogin({ redirectTo })

  const form = useForm({
    defaultValues: { username: '', password: '' },
    validators: { onChange: loginSchema },
    onSubmit: async ({ value }) => {
      try {
        await login.mutateAsync({
          username: value.username.trim(),
          password: value.password.trim(),
        })
      } catch {}
    },
  })

  return (
    <form
      onSubmit={(e: React.FormEvent) => {
        e.preventDefault()
        form.handleSubmit()
      }}
    >
      <div className="flex w-full flex-col gap-3.5 sm:gap-4">
        <form.Field name="username">
          {(field) => (
            <div>
              <label
                htmlFor="username"
                className="mb-1.5 block text-sm sm:text-base font-medium"
              >
                Tên đăng nhập <span className="text-red-500">*</span>
              </label>
              <Input
                id="username"
                type="text"
                size="large"
                value={field.state.value}
                onChange={(e: { target: { value: Updater<string> } }) =>
                  field.handleChange(e.target.value)
                }
                onBlur={field.handleBlur}
                placeholder="Tên đăng nhập"
                autoComplete="off"
              />
              {field.state.meta.isTouched &&
                field.state.meta.errors.length > 0 && (
                  <Text type="danger" className="mt-1 block text-xs sm:text-sm">
                    {String(
                      field.state.meta.errors[0]?.message ??
                        field.state.meta.errors[0],
                    )}
                  </Text>
                )}
            </div>
          )}
        </form.Field>

        <form.Field name="password">
          {(field) => (
            <PasswordField
              label="Mật khẩu"
              required
              id="password"
              value={field.state.value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                field.handleChange(e.target.value)
              }
              onBlur={field.handleBlur}
              placeholder="••••••••"
              autoComplete="off"
              error={
                field.state.meta.isTouched && field.state.meta.errors.length > 0
                  ? String(
                      field.state.meta.errors[0]?.message ??
                        field.state.meta.errors[0],
                    )
                  : undefined
              }
            />
          )}
        </form.Field>

        <div className="-mt-1 text-right">
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 text-[#0088FF] hover:underline"
            onClick={onForgotPassword}
          >
            Quên mật khẩu?
          </button>
        </div>

        <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
          {([canSubmit, isSubmitting]) => (
            <Button
              variant="primary"
              htmlType="submit"
              size="lg"
              block
              loading={isSubmitting || login.isPending}
              className="text-[14px]"
              disabled={!canSubmit}
            >
              Đăng nhập ngay
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form>
  )
}
